/**
 * The preview of each camera, by camera id: the last frame that the server or a tile gave, as a
 * `data:` URL. A member reads sessionStorage once in each store, so a new store never gets the
 * value that the store before it read.
 */

import { type Atom, atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
import { atomWithLazy } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { readStoredPreview, writeStoredPreview } from '@/lib/cameraPreviews';
import type { StoredPreview } from '@/types/devices';

export const cameraPreviewAtom = atomFamily(
    (id: string): PrimitiveAtom<StoredPreview | null> =>
        atomWithLazy((): StoredPreview | null => readStoredPreview(id)));

/** Keeps a new picture of the camera, with the time it arrived, in the store and in storage. */
export const cachePreviewAtom: WritableAtom<null, [string, string], void> = atom(
    null,
    (_get, set, id: string, dataUrl: string): void => {
        const preview = { dataUrl, fetchedAtMs: Date.now() };
        set(cameraPreviewAtom(id), preview);
        writeStoredPreview(id, preview);
    });

export const cameraPreviewUrlAtom = atomFamily((id: string): Atom<string | null> =>
    atom((get): string | null => get(cameraPreviewAtom(id))?.dataUrl ?? null));
